import { defineStore } from 'pinia';
import { useHFetch } from '../composables/http/use-http-request';
import { OnifyAPIEndpoint } from '@/shared/constants/onify-api-endpoint';
import { isPublicAuthPath } from '@/utils/public-auth-path';
import { computed } from 'vue';

export interface IUserSettings {
  key?: string;
  role: string;
  lastworkspace: string;
  settings: {
    locale: string;
    theme: string;
    select_language: boolean;
    select_theme: boolean;
    select_startpage: boolean;
    defaultpage: {
      type: string;
      value: string;
    };
  };
}

/**
 * A Vue composable that provides user settings management functionalities.
 *
 * @function
 * @name useOUserSettingsStore
 * @description
 * This composable provides methods to manage user settings within a Vue application.
 * It includes functionalities to fetch and access user settings.
 *
 * @returns {Object} An object containing the following properties and methods:
 * - `oUserSettings`: A computed property that returns the user settings.
 * - `oRefetchUserSettings`: A function to refetch the user settings from the API.
 */
export const useOUserSettingsStore = defineStore('onify:userSettings', () => {
  const onifyApiRequest = useHFetch();
  const {
    data,
    execute: oRefetchUserSettings,
    loading: oUserSettingsLoading,
  } = onifyApiRequest<IUserSettings>({
    url: OnifyAPIEndpoint.USER_SETTINGS,
    precondition: () => !isPublicAuthPath(),
  });
  const oUserSettings = computed<IUserSettings>(() => (data.value ? data.value : ({} as IUserSettings)));

  return { oUserSettings, oRefetchUserSettings, oUserSettingsLoading };
});
